// src/components/suppliers/ServicesPricingSection.jsx

import React from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    IconButton,
    Alert,
    Chip,
    CircularProgress,
    Skeleton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useTranslation } from "react-i18next";

import FormInput from "../inputs/FormInput";
import MultipleSelect from "../inputs/MultipleSelect";

const ServicesPricingSection = ({
    isDark,
    isLoadingServices,
    servicesOptions,
    selectedServices,
    handleServicesChange,
    servicesPricing,
    handlePricingChange,
    handleRemoveService,
    getServiceName,
    paxTypesOptions,
    isLoadingPaxTypes,
}) => {
    const normalizePriceValue = (value) => {
        if (value === null || value === undefined || value === "") return "";
        const numeric = parseFloat(value);
        if (Number.isNaN(numeric)) return String(value);
        if (numeric === 0) return "0";
        return String(value);
    };
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    // التحقق من صحة الأسعار
    const validatePricing = (pricing) => {
        if (!pricing?.pax_type_prices) return { isValid: true, message: null };
        
        for (const paxPricing of pricing.pax_type_prices) {
            const costPrice = parseFloat(paxPricing.cost_price) || 0;
            const sellPrice = parseFloat(paxPricing.price) || 0;

            if (costPrice > 0 && sellPrice > 0 && costPrice > sellPrice) {
                return {
                    isValid: false,
                    message: isArabic
                        ? "⚠️ سعر التكلفة أعلى من سعر البيع لإحدى أنواع الركاب!"
                        : "⚠️ Cost price is higher than sell price for one of the pax types!",
                };
            }
        }

        return { isValid: true, message: null };
    };

    return (
        <Box>
            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: isDark ? "#10B981" : "#059669"
                }}
            >
                <AttachMoneyIcon fontSize="small" />
                {isArabic ? "الخدمات والأسعار" : "Services & Pricing"}
            </Typography>

            {isLoadingServices ? (
                <Box
                    sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: isDark ? "rgba(16,185,129,0.05)" : "rgba(16,185,129,0.03)",
                        border: `1px solid ${isDark ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.15)"}`,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            {isArabic ? "جاري تحميل الخدمات..." : "Loading services..."}
                        </Typography>
                    </Box>
                    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                </Box>
            ) : (
                <Box
                    sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: isDark ? "rgba(16,185,129,0.05)" : "rgba(16,185,129,0.03)",
                        border: `1px solid ${isDark ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.15)"}`,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                        }}
                    >
                        {isArabic ? "🔍 اختر الخدمات" : "🔍 Select Services"}
                    </Typography>
                    <MultipleSelect
                        name="services"
                        selectedValue={selectedServices}
                        handleSelectChange={handleServicesChange}
                        multiOptions={servicesOptions?.map(s => ({ id: s.value, name: s.label })) || []}
                    />
                </Box>
            )}

            {selectedServices.length > 0 && !isLoadingServices && (
                <Box sx={{ mt: 3 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 3,
                            pb: 2,
                            borderBottom: `2px solid ${isDark ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.15)"}`
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {isArabic ? "💰 تحديد الأسعار للخدمات المختارة" : "💰 Set Pricing for Selected Services"}
                        </Typography>
                        <Chip
                            label={`${selectedServices.length} ${isArabic ? "خدمة" : "Service(s)"}`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>

                    <Grid container spacing={3}>
                        {selectedServices.map((serviceId, idx) => {
                            const pricing = servicesPricing.find(
                                p => String(p.service_id) === String(serviceId)
                            ) || { pax_type_prices: [] };

                            const validation = validatePricing(pricing);

                            return (
                                <Grid item xs={12} md={6} key={serviceId}>
                                    <Card
                                        elevation={2}
                                        sx={{
                                            background: isDark
                                                ? "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.05) 100%)"
                                                : "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(240,253,250,1) 100%)",
                                            border: `1px solid ${!validation.isValid
                                                ? "#EF4444"
                                                : isDark ? "rgba(16,185,129,0.3)" : "rgba(16,185,129,0.2)"}`,
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-4px)",
                                                boxShadow: isDark
                                                    ? "0 8px 24px rgba(16,185,129,0.25)"
                                                    : "0 8px 24px rgba(16,185,129,0.15)",
                                            }
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Chip
                                                        label={`#${idx + 1}`}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="bold"
                                                        sx={{
                                                            background: isDark
                                                                ? "linear-gradient(90deg, #10B981 0%, #34D399 100%)"
                                                                : "linear-gradient(90deg, #059669 0%, #10B981 100%)",
                                                            WebkitBackgroundClip: "text",
                                                            WebkitTextFillColor: "transparent",
                                                        }}
                                                    >
                                                        {getServiceName(serviceId)}
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveService(serviceId)}
                                                    sx={{
                                                        bgcolor: isDark ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.08)",
                                                        "&:hover": {
                                                            bgcolor: isDark ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.15)",
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>

                                            {(paxTypesOptions || []).map((pax) => {
                                                const paxPricing = pricing.pax_type_prices?.find(p => String(p.pax_type_id) === String(pax.value)) || { price: "", cost_price: "" };
                                                const currentPrice = parseFloat(paxPricing.price) || 0;
                                                const currentCost = parseFloat(paxPricing.cost_price) || 0;
                                                const hasProfit = currentPrice > 0 && currentCost > 0 && currentPrice > currentCost;
                                                const profitValue = currentPrice - currentCost;
                                                const profitPercentage = currentCost > 0 ? (profitValue / currentCost) * 100 : 0;
                                                return (
                                                    <Box key={pax.value} sx={{ mb: 3, borderBottom: `1px dashed ${isDark ? "rgb(255 255 255 / 15%)" : "rgb(0 0 0 / 15%)"}`, pb: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "text.primary" }}>
                                                            {pax.label}
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6}>
                                                                <FormInput
                                                                    label={isArabic ? "📊 سعر التكلفة" : "📊 Cost Price"}
                                                                    placeholder="0"
                                                                    type="number"
                                                                    name={`cost_price_${serviceId}_${pax.value}`}
                                                                    value={normalizePriceValue(paxPricing.cost_price)}
                                                                    onChange={(e) => handlePricingChange(serviceId, pax.value, 'cost_price', e.target.value)}
                                                                    required
                                                                    inputProps={{ min: 0, step: "0.01" }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <FormInput
                                                                    label={isArabic ? "💵 سعر البيع" : "💵 Sell Price"}
                                                                    placeholder="0"
                                                                    type="number"
                                                                    name={`price_${serviceId}_${pax.value}`}
                                                                    value={normalizePriceValue(paxPricing.price)}
                                                                    onChange={(e) => handlePricingChange(serviceId, pax.value, 'price', e.target.value)}
                                                                    required
                                                                    inputProps={{ min: 0, step: "0.01" }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        {hasProfit && (
                                                            <Box
                                                                sx={{
                                                                    mt: 1.5,
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    bgcolor: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.1)",
                                                                    border: `1px solid ${isDark ? "rgba(16,185,129,0.4)" : "rgba(16,185,129,0.3)"}`,
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                    {isArabic ? "💹 الربح المتوقع: " : "💹 Expected Profit: "}
                                                                    <Typography
                                                                        component="span"
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: "#10B981",
                                                                            fontWeight: 700
                                                                        }}
                                                                    >
                                                                        {profitValue.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}
                                                                        {" "}({profitPercentage.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 0 })}%)
                                                                    </Typography>
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}

                                            {/* Validation Error Message */}
                                            {!validation.isValid && (
                                                <Alert
                                                    severity="error"
                                                    sx={{
                                                        mt: 0,
                                                        fontSize: "0.75rem",
                                                        py: 0.5,
                                                    }}
                                                >
                                                    {validation.message}
                                                </Alert>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            {selectedServices.length === 0 && !isLoadingServices && (
                <Alert
                    severity="info"
                    sx={{
                        mt: 2,
                        borderRadius: 2,
                        "& .MuiAlert-icon": {
                            fontSize: 28
                        }
                    }}
                >
                    <Typography variant="body2" fontWeight={500}>
                        {isArabic
                            ? "📝 الرجاء اختيار خدمة واحدة على الأقل لإضافة الأسعار"
                            : "📝 Please select at least one service to add pricing"}
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default ServicesPricingSection;
